/**
 * THE MEDIA SLOT CONTRACT — what size an image must be for a given layout slot.
 *
 * This is the ONE home. The gateway's image-generation route reads it, and the
 * frontend reads a GENERATED copy — `apps/fe/src/media-slots.generated.js`,
 * produced by `packages/schema/scripts/derive-media-slots.mjs` and held in step by
 * `media-slots.generated.test.ts`. Same arrangement, for the same reason, as
 * `blocking-rules.generated.js` (`feedback_one_rule_one_place`).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ★ WHY THIS FILE HAD TO BE CREATED RATHER THAN CITED.
 *
 * Omar, 2026-09-05: "An AI-generated image must have the exact dimensions required
 * by the Authoring layout/media slot … This must NOT depend on the author
 * remembering to request the right dimensions in their prompt. It must NOT depend
 * on the AI model interpreting a phrase such as '16:9'."
 *
 * There was no such specification anywhere — not in the Player, not in this
 * package, not in the emitter. What existed was a CONVENTION: five shipped courses
 * used 1024×576 for almost everything (179 of 197 `sequence` images, 63 of 66
 * `horizontal_tabs`, 53 of 58 full-screen backgrounds). A habit of the people who
 * built those courses, which reads exactly like a rule and is not one
 * (`feedback_a_convention_in_the_content_is_not_a_specification`).
 *
 * ★ AND THE RUNTIME ENFORCES NOTHING, WHICH IS WHY A WRONG SIZE FAILS SILENTLY.
 * The Player has ZERO `object-fit` rules. A content image is `<img width:100%>` in
 * a fixed-width container, so THE IMAGE'S OWN RATIO DRIVES THE RENDERED HEIGHT — a
 * wrong-shaped image is not cropped and does not overflow, it just makes that
 * screen taller or shorter than its neighbours. Eight AI-generated images sit in
 * a shipped course today at 1536×1024 inside 1024×576 `sequence` slots
 * (`03_BT_CE/module_25/03A–03C`) and nothing ever flagged them.
 * Full measurements: `wiki/findings/2026-09-05-course-imagery-audit.md`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE EVIDENCE, per slot. `fit` is measured from the Player's own stylesheet
 * (`dynamo-player` / `06_LATEST/css/Components/components.css`); `containerWidthPx`
 * is the CSS container the image is drawn into at the 1024×630 desktop stage.
 *
 * | layout                      | field                | fit       | container | output      |
 * |-----------------------------|----------------------|-----------|-----------|-------------|
 * | `sequence`                  | `imageUrl`           | intrinsic | 1024      | 1024×576    |
 * | `horizontal_tabs`           | `imageUrl`           | intrinsic |  760      | 1024×576    |
 * | `hidden_items`              | `imageUrl`           | intrinsic |  760      | 1024×576    |
 * | `quiz_images`               | `imageUrl`           | intrinsic |  760      | 1024×576    |
 * | `icons_discover`            | `imageUrl`           | intrinsic |  760      | 1024×576 †  |
 * | `fullscreen_text_and_image` | `backgroundImageUrl` | **cover** | viewport  | 1024×576 ‡  |
 * | `vertical_tabs`             | `backgroundImageUrl` | **cover** | viewport  | 1024×576 ‡  |
 * | `text_and_image`            | `imageUrl`           | intrinsic |  512      | ≤1024 wide  |
 *
 * ★ REMOVED 2026-09-07 — `vertical_tabs` · `imageUrl`, "Tab image".
 *   It was never a real slot. A `vertical_tabs` tab carries **only** `tabTitle`
 *   and `tabText`: the schema says so and is `.strict()`
 *   (`VerticalTabsTabSchema`), the emitter writes exactly those two tags
 *   (`emitVerticalTabsTab`), and the authoritative Player fragment
 *   `raw/ux-references/vertical_tabs.txt` shows no image inside a `<tab>`. The
 *   row was a copy of the `horizontal_tabs` line above it — same label, same
 *   container width — and its only effect was that
 *   `POST /courses/:id/generate-image` ACCEPTED the pair, since a slot found in
 *   this table is the only gate before the paid call. It would have taken money
 *   for an image with no field to hold it, no editor control to choose it and no
 *   tag to carry it into the package.
 *   Found 2026-09-07 answering Omar's question about which slots really reach
 *   generation. `media-slots.test.ts` now derives the absence from the schema so
 *   the two cannot drift apart again.
 *
 * † `icons_discover` is a pictogram slot — the corpus uses 74×68 line icons, not
 *   photographs. It takes the ILLUSTRATION route with a transparent background
 *   (Omar, 2026-09-05); the size below is the frame it is composed into.
 * ‡ These two CROP. Measured cost at 1024×576, and the reason `safeArea` exists:
 *     · `fullscreen_text_and_image` — container 1024×530 (`100vh − 100px`),
 *       anchored `top` → loses 46px off the BOTTOM, ~8%.
 *     · `vertical_tabs` background — container 1024×630, anchored `center` →
 *       drawn 1120×630, loses ~48px EACH SIDE, ~9%.
 *   Omar rejected 1920×1080 for these: "I want 1024×576 to be the standard image
 *   size across the layouts, including the background layouts." So the remedy is a
 *   safe area the generator must respect, not a larger asset.
 *
 * ★ `text_and_image` IS THE ONE EXCEPTION, AND IT IS DELIBERATE. Measured on
 * Omar's instruction: that slot has NO `max-width`, NO fixed `height`, NO
 * `min-height` and NO `object-fit` — `.rowContent` is 1024px, the image column is
 * `width:50%` (≈512px), and the image is `width:100%`. An image wider than the
 * column is scaled down, never cropped; the height follows the original ratio.
 * His decision: "the maximum image width should be 1024px; the height should not
 * have a fixed required value; the image should preserve its original aspect
 * ratio." ⚠️ This SUPERSEDES his own earlier answer of "Upright 840×730", chosen
 * from the corpus convention before the Player had been re-measured — a fixed
 * height there would have been a requirement we invented and then enforced.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * DELIBERATELY ABSENT — each an explicit decision, not an oversight:
 *
 *   · `videoThumbUrl`, EVERY layout — AI generation of video thumbnails is
 *     excluded PERMANENTLY (Omar, 2026-09-05): "The Authoring Tool already gives
 *     the user two sufficient options: upload an image; select a frame from the
 *     video. These two existing functions are enough." A product decision, not
 *     deferred video work. `slotAcceptsGeneratedImage` returns false for it.
 *   · `hidden_items.image360Url` — equirectangular panorama (the corpus ships
 *     5760×2880 and 3600×1800). A different generation problem; out of scope.
 *   · `object_viewer.backgroundImageUrl` — the 3D viewer's cover; out of scope.
 *   · `gamingHeader.imgUrlCorrect` / `imgUrlWrong` — DFTI gaming furniture, and
 *     language-independent assets with their own placement rule.
 *   · `title.backgroundImageUrl` — EXISTS in the schema, but has **no sizing rule
 *     in the Player stylesheet** and **zero uses across the five-course corpus**,
 *     so there is nothing to derive a contract from. Left unspecified rather than
 *     guessed; add it when a real use gives it evidence.
 */
/** The standard output for every fixed-format slot. One number, everywhere. */
export const STANDARD_IMAGE_WIDTH = 1024;
export const STANDARD_IMAGE_HEIGHT = 576;
/**
 * The slots an author can generate an image into. Ordered as the evidence table
 * above, fixed-size first.
 */
export const MEDIA_SLOTS = Object.freeze([
    {
        layoutType: "sequence",
        field: "imageUrl",
        label: "Step image",
        fit: "intrinsic",
        widthPx: STANDARD_IMAGE_WIDTH,
        heightPx: STANDARD_IMAGE_HEIGHT,
        containerWidthPx: 1024,
        defaultStyle: "photograph",
    },
    {
        layoutType: "horizontal_tabs",
        field: "imageUrl",
        label: "Tab image",
        fit: "intrinsic",
        widthPx: STANDARD_IMAGE_WIDTH,
        heightPx: STANDARD_IMAGE_HEIGHT,
        containerWidthPx: 760,
        defaultStyle: "photograph",
    },
    {
        layoutType: "hidden_items",
        field: "imageUrl",
        label: "Question image",
        fit: "intrinsic",
        widthPx: STANDARD_IMAGE_WIDTH,
        heightPx: STANDARD_IMAGE_HEIGHT,
        containerWidthPx: 760,
        defaultStyle: "photograph",
    },
    {
        layoutType: "quiz_images",
        field: "imageUrl",
        label: "Question image",
        fit: "intrinsic",
        widthPx: STANDARD_IMAGE_WIDTH,
        heightPx: STANDARD_IMAGE_HEIGHT,
        containerWidthPx: 760,
        defaultStyle: "photograph",
    },
    {
        layoutType: "icons_discover",
        field: "imageUrl",
        label: "Icon",
        fit: "intrinsic",
        widthPx: STANDARD_IMAGE_WIDTH,
        heightPx: STANDARD_IMAGE_HEIGHT,
        containerWidthPx: 760,
        defaultStyle: "illustration",
    },
    {
        layoutType: "fullscreen_text_and_image",
        field: "backgroundImageUrl",
        label: "Background image",
        fit: "cover",
        anchor: "top",
        widthPx: STANDARD_IMAGE_WIDTH,
        heightPx: STANDARD_IMAGE_HEIGHT,
        containerWidthPx: 1024,
        // 46 of 576 rows are lost off the bottom at the 1024×530 container.
        safeArea: { bottom: 0.08 },
        textRegion: { left: 0.24, top: 0.18, right: 0.73, bottom: 0.37 },
        defaultStyle: "photograph",
    },
    {
        layoutType: "vertical_tabs",
        field: "backgroundImageUrl",
        label: "Background image",
        fit: "cover",
        anchor: "center",
        widthPx: STANDARD_IMAGE_WIDTH,
        heightPx: STANDARD_IMAGE_HEIGHT,
        containerWidthPx: 1024,
        // Drawn 1120×630 into 1024×630: ~48px lost from each side.
        safeArea: { left: 0.09, right: 0.09 },
        defaultStyle: "photograph",
    },
    {
        // ★ The one variable-height slot. See the header — measured, not assumed.
        layoutType: "text_and_image",
        field: "imageUrl",
        label: "Row image",
        fit: "intrinsic",
        widthPx: STANDARD_IMAGE_WIDTH,
        // heightPx deliberately absent: the Player constrains no height here.
        containerWidthPx: 512,
        defaultStyle: "photograph",
    },
]);
/**
 * Fields that never accept a generated image, with the reason. Kept as data so a
 * caller gets an explanation rather than an unexplained `undefined`
 * (`feedback_a_new_gate_needs_its_remedy_to_exist`).
 */
export const NON_GENERATED_IMAGE_FIELDS = Object.freeze({
    videoThumbUrl: "Video thumbnails are not AI-generated. Upload an image, or capture a frame from the video.",
    image360Url: "360° panoramas need an equirectangular projection, which is out of scope.",
    imgUrlCorrect: "Gaming-quiz artwork is not AI-generated.",
    imgUrlWrong: "Gaming-quiz artwork is not AI-generated.",
});
/** Look up one slot. `undefined` means "not a generatable image slot". */
export function findMediaSlot(layoutType, field) {
    return MEDIA_SLOTS.find((s) => s.layoutType === layoutType && s.field === field);
}
/** Can an author generate into this slot at all? */
export function slotAcceptsGeneratedImage(layoutType, field) {
    return findMediaSlot(layoutType, field) !== undefined;
}
/**
 * Why a field cannot be generated into. Returns the specific reason where one is
 * recorded, and a general one otherwise — never an empty string, because this is
 * what the author reads.
 */
export function whyNotGeneratable(layoutType, field) {
    if (slotAcceptsGeneratedImage(layoutType, field))
        return "";
    return (NON_GENERATED_IMAGE_FIELDS[field] ??
        `AI image generation is not available for ${layoutType}.${field}.`);
}
/** Is this slot's output an exact size, or a maximum width with free height? */
export function slotHasExactSize(spec) {
    return spec.heightPx !== undefined;
}
/**
 * What the author is TOLD. Never a question — Omar's rule is that the size is
 * derived, so this is a statement of what will happen.
 */
export function describeSlotSize(spec) {
    return slotHasExactSize(spec)
        ? `This slot needs ${spec.widthPx} × ${spec.heightPx} — handled for you.`
        : `Up to ${spec.widthPx} px wide — the height follows your image.`;
}
/**
 * Does a finished asset satisfy the slot? The check the deterministic tail runs
 * against the STORED bytes, not against what was requested.
 *
 * ⚠️ Deliberately strict on the exact-size slots and deliberately loose on the
 * variable one: `text_and_image` accepts ANY height, because the Player accepts
 * any height there. Asserting a height would re-introduce the invented
 * requirement this contract exists to avoid.
 */
export function assetFitsSlot(spec, actual) {
    if (!Number.isInteger(actual.width) || !Number.isInteger(actual.height)) {
        return { ok: false, reason: "Image dimensions must be whole pixels." };
    }
    if (actual.width <= 0 || actual.height <= 0) {
        return { ok: false, reason: "Image dimensions must be positive." };
    }
    if (slotHasExactSize(spec)) {
        const want = `${spec.widthPx}×${spec.heightPx}`;
        const got = `${actual.width}×${actual.height}`;
        return want === got
            ? { ok: true }
            : { ok: false, reason: `${spec.layoutType}.${spec.field} needs exactly ${want}; got ${got}.` };
    }
    return actual.width <= spec.widthPx
        ? { ok: true }
        : {
            ok: false,
            reason: `${spec.layoutType}.${spec.field} allows at most ${spec.widthPx} px wide; got ${actual.width}.`,
        };
}
//# sourceMappingURL=media-slots.js.map